import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Button, Link, TextField, Typography } from '@mui/material';

import theme from '../../styles/MaterialTheme';
import CustomThemeProvider from '../../utils/CustomThemeProvider';
import PasswordResetByCode from './PasswordResetByCode';
import { Flex } from '../../components';
import LogoFull from '../../assets/images/logo-full.png';

const FIREBASE_ERROR_MESSAGES = {
  'auth/wrong-password': 'Invalid email or password',
  'auth/user-not-found': 'Invalid email or password',
  'auth/invalid-credential': 'Invalid email or password',
  'auth/too-many-requests': 'Too many attempts. Please try again later.',
  'auth/invalid-email': 'Please enter a valid email address',
};

const LoginContent = ({
  onClick = (f) => f,
  onEmailLogin = null,
  logoUrl = null,
  provider,
  directIdpName = null,
  showPasswordReset = false,
  customSiteText = {},
}) => {
  const [isEnteringCode, setIsEnteringCode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setEmailError('Please enter email and password');
      return;
    }
    setEmailError('');
    setIsSubmitting(true);
    try {
      await onEmailLogin(email, password);
    } catch (err) {
      const message = FIREBASE_ERROR_MESSAGES[err.code] || 'Login failed. Please try again.';
      setEmailError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomThemeProvider theme={theme} productionPrefix="Routing-JSS-">
      <Flex.V
        sx={{
          height: '100vh',
          background: '#fff',
          padding: '40px 20px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '32px',
        }}
      >
        {/* Logo Section */}
        <Flex
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <img
            src={logoUrl || LogoFull}
            alt="BlackBoiler Logo"
            data-cy="company-logo"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = LogoFull;
            }}
            style={{
              maxWidth: '320px',
              width: '100%',
              height: 'auto',
            }}
          />
        </Flex>

        {/* Welcome Text */}
        <Flex.V sx={{ gap: '8px', alignItems: 'center' }}>
          <Typography
            variant="h1"
            align="center"
            sx={{ fontWeight: 600, color: '#1a1a1a' }}
          >
            Welcome to BlackBoiler
          </Typography>

          <Typography variant="h5" align="center" sx={{ color: '#666' }}>
            Sign in to access your contract review platform
          </Typography>
        </Flex.V>

        {/* Sign In Button Section */}
        {provider === 'gcp' && onEmailLogin ? (
          <Flex.V
            sx={{
              width: '100%',
              maxWidth: '400px',
              gap: '16px',
            }}
          >
            <Box component="form" onSubmit={handleEmailLogin} sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                autoComplete="email"
                size="small"
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                autoComplete="current-password"
                size="small"
              />
              {emailError && (
                <Typography sx={{ color: '#d32f2f', fontSize: '14px', textAlign: 'center' }}>
                  {emailError}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {isSubmitting ? 'Signing in...' : 'LOGIN'}
              </Button>
            </Box>

            <Flex.H sx={{ alignItems: 'center', gap: '12px' }}>
              <Box sx={{ flex: 1, height: '1px', backgroundColor: '#ccc' }} />
              <Typography sx={{ color: '#999', fontSize: '13px' }}>or</Typography>
              <Box sx={{ flex: 1, height: '1px', backgroundColor: '#ccc' }} />
            </Flex.H>

            <Button
              variant="outlined"
              color="secondary"
              onClick={onClick}
              fullWidth
              sx={{
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              }}
            >
              BLACKBOILER
            </Button>
          </Flex.V>
        ) : directIdpName && provider === 'aws' ? (
          <Flex.V
            sx={{
              width: '100%',
              maxWidth: '400px',
              gap: '12px',
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={() => onClick(null, true)}
              fullWidth
              sx={{
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              LOGIN
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={onClick}
              fullWidth
              sx={{
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              }}
            >
              LOGIN WITH ANOTHER PROVIDER
            </Button>
          </Flex.V>
        ) : (
          <Flex.V
            sx={{
              width: '100%',
              maxWidth: '400px',
              gap: '16px',
            }}
          >
            {isEnteringCode ? null : (
              <Button
                data-cy="sign-in-button"
                variant="contained"
                color="secondary"
                onClick={onClick}
                fullWidth
                sx={{
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                LOGIN
              </Button>
            )}

            {showPasswordReset ? (
              <Flex.V sx={{ alignItems: 'center', justifyContent: 'center' }}>
                {!isEnteringCode ? (
                  <button
                    type="button"
                    onClick={() => setIsEnteringCode(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '8px',
                    }}
                  >
                    <Typography sx={{ color: '#1976d2', fontSize: '14px' }}>
                      Enter recovery code
                    </Typography>
                  </button>
                ) : (
                  <PasswordResetByCode
                    setDisplay={setIsEnteringCode}
                    onClick={
                      directIdpName && provider === 'aws'
                        ? () => onClick(null, true)
                        : onClick
                    }
                  />
                )}
              </Flex.V>
            ) : null}
          </Flex.V>
        )}

        {/* Custom Site Text */}
        {Object.keys(customSiteText).length && !customSiteText.hidden ? (
          <Flex sx={{ marginTop: '24px' }}>
            {customSiteText.type === 'TEXT' ? (
              <Typography align="center" sx={{ color: '#666' }}>
                {customSiteText.text}
              </Typography>
            ) : null}
            {customSiteText.type === 'LINK' ? (
              <Typography align="center">
                <Link
                  href={customSiteText.linkDest}
                  underline="hover"
                  sx={{ color: '#1976d2' }}
                >
                  {customSiteText.text}
                </Link>
              </Typography>
            ) : null}
          </Flex>
        ) : null}

        {/* Footer Links */}
        <Flex.H
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            marginTop: '16px',
            paddingBottom: '40px',
          }}
        >
          <Typography variant="body2">
            <Link
              href="https://www.blackboiler.com/blackboiler-llterms-of-use/"
              underline="hover"
            >
              Terms of Use
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            •
          </Typography>
          <Typography variant="body2">
            <Link
              href="https://www.blackboiler.com/intellectual-property/"
              underline="hover"
            >
              Intellectual Property
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            •
          </Typography>
          <Typography variant="body2">
            <Link href="https://www.blackboiler.com/privacy-policy/" underline="hover">
              Privacy Policy
            </Link>
          </Typography>
        </Flex.H>
      </Flex.V>
    </CustomThemeProvider>
  );
};

LoginContent.propTypes = {
  onClick: PropTypes.func,
  onEmailLogin: PropTypes.func,
  logoUrl: PropTypes.string,
  provider: PropTypes.string.isRequired,
  directIdpName: PropTypes.string,
  showPasswordReset: PropTypes.bool,
  customSiteText: PropTypes.object,
};

export default LoginContent;
